import * as THREE from "three";
import MakePart from "../../Experience/World/MakePart";
import debug from "debug";
const log = debug("app:ur");


/**
 * Manages the copy and paste functionality for selected objects in the 3D scene.
 * @category Experience
 * @subcategory ObjectManagement
 */
class CopyPasteManager {
  /**
   * Constructs a CopyPasteManager instance.
   * @param {Object} experience - The main application experience object.
   * @param {THREE.Scene} experience.scene - The 3D scene containing objects.
   * @param {Object} experience.eventHub - Event hub for managing custom events.
   * @param {Object} experience.camera - Camera instance for the 3D scene.
   */
  constructor(experience) {
    this.experience = experience;
    this.scene = experience.scene;
    this.camera = experience.camera.instance;
    this.raycaster = new THREE.Raycaster();
    this.mouse = new THREE.Vector2();
    this.copiedObjectsData = []; // Initialize copiedObjectsData as an array
    this.eventHub = experience.eventHub;

    this.init();
  }

  cleanup() {
    console.log("Cleaning up CopyPasteManager...");

    // Remove event listeners
    this.eventHub.off("globalKeyDown", this.handleKeyDown);
    this.eventHub.off("mousemove", this.handleMouseMove);
    this.eventHub.off("copyCM", this.copySelectedObjects);
    this.eventHub.off("pasteCM", this.pasteCopiedObjects);

    const duplicateOption = document.getElementById("duplicateOption");
    if (duplicateOption) {
        duplicateOption.removeEventListener("click", this.copySelectedObjects);
        duplicateOption.removeEventListener("click", this.pasteCopiedObjects);
    }

    // Clear stored references
    this.copiedObjectsData = [];
    this.scene = null;
    this.camera = null;
    this.eventHub = null;
    this.experience = null;

    console.log("CopyPasteManager cleanup complete.");
}

  /**
   * Initializes the copy-paste manager by setting up necessary event listeners.
   */
  init() {
    this.eventHub.on("globalKeyDown", this.handleKeyDown.bind(this));
    this.eventHub.on("mousemove", this.handleMouseMove.bind(this));
    this.eventHub.on("copyCM", () => {
      this.copySelectedObjects();
    });
    this.eventHub.on("pasteCM", () => {
      this.pasteCopiedObjects();
    });
    document.getElementById("duplicateOption").addEventListener("click", () => {
      this.copySelectedObjects();
      this.pasteCopiedObjects();
    });
  }

  /**
   * Handles global keydown events for copy (`Ctrl+C`) and paste (`Ctrl+V`) operations.
   * @param {KeyboardEvent} event - The keyboard event object.
   */
  handleKeyDown(event) {
    if (event.ctrlKey || event.metaKey) {
      switch (event.key.toLowerCase()) {
        case "c":
          this.copySelectedObjects();
          break;
        case "v":
          this.pasteCopiedObjects();
          break;
      }
    }
  }

  /**
   * Tracks mouse movement to update the mouse position vector.
   * @param {MouseEvent} event - The mouse event object.
   */
  handleMouseMove(event) {
    this.mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
    this.mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;
  }

  /**
   * Copies the currently selected objects in the scene.
   * Stores user data, position, and rotation (quaternion) of each selected object.
   */
  copySelectedObjects() {
    const selectedObjects = Array.from(this.experience.selectedObjects);

    if (selectedObjects.length > 0) {
      log("selected objects", selectedObjects);
      console.log(
        "Objects data copied:",
        selectedObjects.map((obj) => obj.userData)
      );

      this.copiedObjectsData = selectedObjects.map((obj) => ({
        userData: obj.userData,
        position: obj.position.clone(),
        quaternion: obj.quaternion.clone(), // Store rotation using quaternion
      }));
    } else {
      log(" no selected objects", this.experience.selectedObjects);
    }
  }

  /**
   * Pastes the previously copied objects into the scene.
   * Adjusts positions based on the current mouse intersection point with the scene.
   */
  async pasteCopiedObjects() {
    log("paste started", this.copiedObjectsData);

    if (this.copiedObjectsData.length > 0) {
      const intersects = this.experience.mouseTracker.getIntersection(
        this.scene.children
      );
      let basePosition = new THREE.Vector3();

      if (intersects.length > 0) {
        basePosition.copy(intersects[0].point);
        basePosition.x = Math.round(intersects[0].point.x);
        basePosition.z = Math.round(intersects[0].point.z);
        basePosition.y = 0.3; // Ensure objects are placed at the correct Y level
      }

      const xmdData = { figures: [], others: [] };

      for (const data of this.copiedObjectsData) {
        const offset = data.position
          .clone()
          .sub(this.copiedObjectsData[0].position);
        const newPosition = basePosition.clone().add(offset);
        newPosition.y = data.position.y; // Inherit Y from the original

        if (data.userData.mode === "textbox") {
          xmdData.others.push({
            ...data.userData, // Flat structure for textbox
            x: newPosition.x,
            y: newPosition.y,
            z: newPosition.z,
            angle: THREE.MathUtils.radToDeg(
              new THREE.Euler().setFromQuaternion(data.quaternion, "YZX").y
            ),
          });
        } else {
          xmdData.figures.push({
            partData: data.userData, // Save user data for figures
            x: newPosition.x,
            y: newPosition.y,
            z: newPosition.z,
            angle: THREE.MathUtils.radToDeg(
              new THREE.Euler().setFromQuaternion(data.quaternion, "YZX").y
            ),
          });
        }
      }

      // Emit a single event with both figures and others
      this.eventHub.emit("MakePartsEvent", {
        eventType: "copy",
        data: xmdData,
      });
    }
  }
}

export default CopyPasteManager;
